import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {MapsAPILoader} from '@agm/core';
import {isDefined} from '../../commons/commons';
import {ECPodCoordinates, SpatialCoordinates} from 'smag-commons/cfg/hierarchy';
import {ECNetworkSpatialCoordinates} from '../../views/dev-dashboard/dev-dashboard.component';

const INITIAL_ZOOM = 16;

@Component({
    selector: 'app-google-maps',
    templateUrl: './google-maps.component.html',
    styleUrls: ['./google-maps.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoogleMapsComponent implements OnInit, OnDestroy, OnChanges {
    private map: google.maps.Map;
    private autocomplete: google.maps.places.Autocomplete;
    private autocompleteListener: google.maps.MapsEventListener;
    private clickListener: google.maps.MapsEventListener;
    private oldMarkers: google.maps.Marker[];
    private newMarkers: google.maps.Marker[];

    @ViewChild('search') private searchElementRef: ElementRef;
    @Input() coordinates: SpatialCoordinates;
    @Input() ecPodCoordinates: ECPodCoordinates[];
    @Output() coordinatesChange: EventEmitter<SpatialCoordinates> = new EventEmitter<SpatialCoordinates>();
    @Input() chooseAddress: boolean; // must not change at runtime
    @Input() showMarker: boolean;
    @Input() locations: ECNetworkSpatialCoordinates[];

    // exported in HTML
    INITIAL_ZOOM = INITIAL_ZOOM;

    constructor(private mapsAPILoader: MapsAPILoader,
                private ngZone: NgZone,
                private changeDetectorRef: ChangeDetectorRef) {
        this.map = null;
        this.autocomplete = null;
        this.autocompleteListener = null;
        this.clickListener = null;
        this.oldMarkers = [];
        this.newMarkers = [];
    }

    ngOnInit() {
        if (this.chooseAddress) {
            this.mapsAPILoader.load().then(() => {
                this.autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement);
                this.autocompleteListener = this.autocomplete.addListener('place_changed', () => {
                    this.ngZone.run(() => {
                        // get the place result
                        const place: google.maps.places.PlaceResult = this.autocomplete.getPlace();
                        // verify result
                        if (!isDefined(place.geometry)) {
                            return;
                        }
                        // set latitude, longitude
                        this.coordinates = {
                            latitude: place.geometry.location.lat(),
                            longitude: place.geometry.location.lng()
                        };
                        this.coordinatesChange.next(this.coordinates);
                        this.changeDetectorRef.markForCheck();
                    });
                });
            });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('locations' in changes && !changes.locations.isFirstChange()) {
            this.locations = changes.locations.currentValue;
            this.addMarkers();
            this.removeMarkers();
        }

        if ('ecPodCoordinates' in changes && !changes.ecPodCoordinates.isFirstChange()) {
            this.ecPodCoordinates = changes.ecPodCoordinates.currentValue;
            this.addMarkers();
            this.removeMarkers();
        }
    }

    ngOnDestroy() {
        if (this.autocomplete) {
            this.autocomplete.unbindAll();
        }
        if (this.autocompleteListener) {
            this.autocompleteListener.remove();
        }
        if (this.clickListener) {
            this.clickListener.remove();
        }
    }

    onMapReady(map: google.maps.Map) {
        this.map = map;
        // remove poi likes hotels
        this.map.setOptions({styles: [{featureType: 'poi', stylers: [{visibility: 'off'}]}], mapTypeId: google.maps.MapTypeId.ROADMAP});
        if (this.chooseAddress) {
            this.clickListener = this.map.addListener('rightclick', (event: google.maps.MouseEvent) => {
                this.ngZone.run(() => {
                    this.coordinates = {
                        latitude: event.latLng.lat(),
                        longitude: event.latLng.lng()
                    };
                    this.coordinatesChange.next(this.coordinates);
                    this.changeDetectorRef.markForCheck();
                });
            });
        }
        this.addMarkers();
    }

    onMarkerDragEnd(event: google.maps.MouseEvent) {
        this.coordinates = {
            latitude: event.latLng.lat(),
            longitude: event.latLng.lng()
        };
        this.coordinatesChange.next(this.coordinates);
        this.changeDetectorRef.markForCheck();
    }

    addMarkers() {
        this.oldMarkers = this.newMarkers;
        this.newMarkers = [];
        if (this.locations !== undefined && this.locations.length > 0) {
            const coordinates = this.locations.map((ec) => ec.spatial_coordinates);
            this.newMarkers = this.generateMarkers(coordinates);
        }
        if (this.ecPodCoordinates !== undefined && this.ecPodCoordinates.length > 0) {
            const coordinates = this.ecPodCoordinates.map((pod) => pod.spatial_coordinates);
            this.newMarkers = this.generateMarkers(coordinates, true);
        }
    }

    removeMarkers() {
        const diff = this.oldMarkers.filter(x => !this.newMarkers.includes(x));
        diff.forEach(d => d.setMap(null));
    }

    private setMarkerLabel(ecPodCoordinates: ECPodCoordinates) {
        if (ecPodCoordinates.first_name && ecPodCoordinates.last_name) {
            return `${ecPodCoordinates.first_name} ${ecPodCoordinates.last_name} ${ecPodCoordinates.pod}`;
        }
        if (!ecPodCoordinates.first_name && !ecPodCoordinates.last_name && ecPodCoordinates.company_name) {
            return `${ecPodCoordinates.company_name} ${ecPodCoordinates.pod}`;
        }
        return '';
    }
    private generateMarkers(coordinates: SpatialCoordinates[], podMarkers: boolean = false) {
        if (this.map) {
            const bounds = new google.maps.LatLngBounds();
            const newMarkers: google.maps.Marker[] = [];
            if (this.coordinates) {
                const newPoint = new google.maps.LatLng(this.coordinates.latitude, this.coordinates.longitude);
                bounds.extend(newPoint);
            }
            coordinates.forEach((l, i) => {
                const marker = new google.maps.Marker({
                    position: {lat: l.latitude, lng: l.longitude},
                    map: this.map,
                    icon: podMarkers ? 'assets/pod-marker.svg' : 'assets/main-marker.svg',
                    title: podMarkers ? this.setMarkerLabel(this.ecPodCoordinates[i]) : this.locations[i].name
                });
                newMarkers.push(marker);
                bounds.extend(marker.getPosition());
            });
            this.map.setCenter(bounds.getCenter());
            this.map.fitBounds(bounds);
            // if you only got 1 marker or all markers are on the same address the map will be zoomed too much
            if (this.map.getZoom() > INITIAL_ZOOM) {
                this.map.setZoom(INITIAL_ZOOM);
            }
            return newMarkers;
        }
        return [];
    }
}

